import { format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Icon, Loading, Spinner, Tooltip, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useProfile } from "@/hooks";
import { useGetVehicleFormOptionsQuery, useUpdateVehicleMutation, useVerifyVehicleMutation, vehicleApi } from "@/redux/apis/vehicle/vehicleApi";
import { useAppDispatch } from "@/redux/hooks";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { addToast, formatCurrency } from "@/utils";
import { getCountryFlagSrc } from "@/utils/getCountryFlagSrc";

interface Step4ReviewProps {
  vehicleId: string;
  vehicle: Vehicle;
  onBack: () => void;
  onFinishLater: () => void;
}

const renderFileIcon = (filename: string) => {
  const ext = filename.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf":
      return <Icon name="file-pdf" size="md" isCustom />;
    case "jpg":
    case "jpeg":
      return <Icon name="file-jpg" size="md" isCustom />;
    case "png":
      return <Icon name="file-png" size="md" isCustom />;
    default:
      return <Icon name="file" size="md" isCustom />;
  }
};

export const Step4Review = ({ vehicleId, vehicle, onBack, onFinishLater }: Step4ReviewProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { dateFormat } = useProfile();
  const [updateVehicle, { isLoading: isUpdating }] = useUpdateVehicleMutation();
  const [verifyVehicle, { isLoading: isVerifying }] = useVerifyVehicleMutation();
  const { data: options } = useGetVehicleFormOptionsQuery();
  const [error, setError] = useState<string | null>(null);
  const flagSrc = getCountryFlagSrc("AU");

  const fee = options?.fees.verificationFee ?? 0;
  const isLoading = isUpdating || isVerifying;

  const insuranceDoc = vehicle.documents.find((d) => d.documentType === "INSURANCE");
  const accreditationDoc = vehicle.documents.find((d) => d.documentType === "ACCREDITATION");

  const handleConfirm = async () => {
    setError(null);

    // Check if insurance has expired since step 3
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (insuranceDoc?.expiryDate && new Date(insuranceDoc.expiryDate) < today) {
      addToast("danger", "Your insurance document has expired. Please go back to Step 3 and upload a valid document.");
      return;
    }
    if (accreditationDoc?.expiryDate && new Date(accreditationDoc.expiryDate) < today) {
      addToast("danger", "Your accreditation document has expired. Please go back to Step 3 and upload a valid document.");
      return;
    }

    try {
      // Verify vehicle registration first (looks up expiry date)
      await verifyVehicle(vehicleId).unwrap();

      // Mark setup as complete only after verify succeeds
      await updateVehicle({
        id: vehicleId,
        data: {
          identifier: vehicle.identifier,
          capacity_pax: vehicle.capacity.pax,
          capacity_bags: vehicle.capacity.bags,
          maintenance_fee: vehicle.maintenanceFee,
          setup_step: null,
          documents: vehicle.documents.map((d) => ({
            uuid: d.uuid,
            expiry_date: d.expiryDate,
            document_type: d.documentType,
          })),
          categories: vehicle.categories.map((c) => ({ uuid: c.uuid })),
        },
      }).unwrap();

      addToast("success", "Vehicle added successfully");
      dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
      navigate("../");
    } catch (err: unknown) {
      const messages = getErrorMessages(err as Parameters<typeof getErrorMessages>[0]);
      setError(messages[0] || "Failed to register vehicle");
    }
  };

  return (
    <div className="relative flex flex-col gap-6">
      {isLoading && <Loading />}

      {/* Info Banner */}
      <div className="rounded-lg bg-neutral-surface-gray p-4 text-center">
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          Take a moment to review the details entered before adding this vehicle to your account. Once confirmed, your vehicle will be added
          and verified for use.
        </Typography>
        <Typography variant="paragraph" className="mt-3 text-info">
          A one-time <span className="font-semibold">{formatCurrency(fee, 2, "AUD")}</span> fee will be debited during the daily accounting
          processing.
        </Typography>
      </div>

      {error && <Alert type="danger" message={error} />}

      {/* Review Details */}
      <div className="grid grid-cols-2 gap-3">
        <DetailRow label="Make" value={vehicle.model.make} />
        <DetailRow label="Model" value={vehicle.model.model} />
        <DetailRow label="Plate Number" value={vehicle.registrationNumber} />
        <DetailRow label="Year" value={vehicle.year?.toString() ?? "-"} />
        <DetailRow
          label="Color"
          value={
            vehicle.color ? (
              <span className="inline-flex items-center gap-2 capitalize">
                <span
                  className="inline-block h-3 w-3 rounded-full border border-black"
                  style={{ backgroundColor: vehicle.color.toLowerCase().replace("_", "") }}
                />
                {vehicle.color.toLowerCase().replace("_", " ")}
              </span>
            ) : (
              "-"
            )
          }
        />
        <DetailRow label="Engine Type" value={vehicle.model.engine ? vehicle.model.engine.toLowerCase() : "-"} />
        <DetailRow label="Passenger Capacity" value={`${vehicle.capacity.pax} pax`} />
        <DetailRow label="Bag Capacity" value={vehicle.capacity.bags.toString()} />
        <DetailRow
          label="State Registered"
          value={
            <span className="inline-flex items-center gap-1">
              {flagSrc && <img src={flagSrc} className="h-[18px] w-[18px] rounded-full object-cover" alt="" />}
              {vehicle.state.abbreviation}
            </span>
          }
        />
        <DetailRow
          label="Registration Expiry"
          value={vehicle.registration.expiryDate ? format(new Date(vehicle.registration.expiryDate), dateFormat) : "-"}
        />
        <DetailRow label="VIN" value={vehicle.vin ?? "-"} />
        <DetailRow
          label={
            <span className="inline-flex items-center gap-1">
              Vehicle Category
              <Tooltip content="RideMinder's system category used for matching vehicles to network jobs" placement="top">
                <Icon name="InfoCircle" variant="Bold" className="rotate-180 text-neutral-dark-gray" size="sm" />
              </Tooltip>
            </span>
          }
          value={vehicle.vehicleCategory?.name ?? "-"}
        />
        <DetailRow
          label={
            <span className="inline-flex items-center gap-1">
              Vehicle Type
              <Tooltip content="The type classification of your vehicle." placement="top">
                <Icon name="InfoCircle" variant="Bold" className="rotate-180 text-neutral-dark-gray" size="sm" />
              </Tooltip>
            </span>
          }
          value={vehicle.vehicleType?.name ?? "-"}
        />
        <DetailRow label="Vehicle Nickname" value={vehicle.identifier || "-"} />
        <DetailRow label="Vehicle has markings (Signage/Decals)?" value={vehicle.hasMarkings ? "Yes" : "No"} />
        <DetailRow label="Vehicle Owner Maintenance Fee" value={vehicle.maintenanceFee > 0 ? `${vehicle.maintenanceFee}%` : "-"} />
        <DetailRow label="Access Level" value={vehicle.accessType === "ALL" ? "All Drivers" : "Restricted"} />
        {insuranceDoc && (
          <DetailRow
            label="Insurance Document"
            value={
              <span className="inline-flex items-center gap-1.5">
                {renderFileIcon(insuranceDoc.filename)}
                <span className="max-w-[150px] truncate">{insuranceDoc.filename}</span>
              </span>
            }
          />
        )}
        {accreditationDoc && (
          <DetailRow
            label="Accreditation Document"
            value={
              <span className="inline-flex items-center gap-1.5">
                {renderFileIcon(accreditationDoc.filename)}
                <span className="max-w-[150px] truncate">{accreditationDoc.filename}</span>
              </span>
            }
          />
        )}
      </div>

      <div className="mt-4 flex flex-col-reverse gap-3 xl:flex-row xl:justify-between xl:gap-4">
        <Button type="button" variant="custom" size="xs" className="text-neutral-silver-gray underline xl:w-auto" onClick={onFinishLater}>
          Finish later
        </Button>
        <div className="flex flex-col-reverse gap-3 xl:flex-row xl:gap-4">
          <Button type="button" variant="secondary" size="lg" className="w-full xl:w-auto" startIcon="ArrowLeft" onClick={onBack}>
            Back
          </Button>
          <Button type="button" variant="primary" size="lg" className="w-full xl:w-auto" onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? <Spinner /> : `Confirm Registration for ${formatCurrency(fee, 2, "AUD")}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => (
  <>
    <div className="col-span-1 flex items-center">
      <Typography className="text-neutral-dark-gray">{label}</Typography>
    </div>
    <div className="col-span-1 flex items-center justify-end">
      <Typography className="capitalize text-neutral-black">{value}</Typography>
    </div>
  </>
);
